'use client';
import React, { useEffect, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface GoldPrice {
  price: number;
  change: number;
  changePercent: number;
}

const HeroSection: React.FC = () => {
  const [goldPrice, setGoldPrice] = useState<GoldPrice>({
    price: 2043.75,
    change: 12.35,
    changePercent: 0.61
  });
  const [scrollY, setScrollY] = useState(0);

  // Parallax scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Mock real-time price updates
  useEffect(() => {
    const interval = setInterval(() => {
      setGoldPrice((prev) => {
        const randomChange = (Math.random() - 0.5) * 5;
        const newPrice = prev.price + randomChange;
        const newChange = newPrice - 2031.4;
        const newChangePercent = newChange / 2031.4 * 100;

        return {
          price: parseFloat(newPrice.toFixed(2)),
          change: parseFloat(newChange.toFixed(2)),
          changePercent: parseFloat(newChangePercent.toFixed(2))
        };
      });
    }, 15000); // Update every 15 seconds

    return () => clearInterval(interval);
  }, []);

  const handleRequestQuote = () => {
    window.open('https://wa.me/243996739468?text=Hello, I would like to request a quote for gold export.', '_blank');
  };

  const handleViewPrices = () => {
    document.getElementById('pricing-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex flex-col justify-end p-6 md:p-10 pt-40 overflow-hidden">
      {/* Background Image with Cinematic Animation and Parallax */}
      <div 
        className="absolute inset-0 z-0"
        style={{ transform: `translateY(${scrollY * 0.5}px)` }}
      >
        <AppImage
          src="https://img.rocket.new/generatedImages/rocket_gen_img_1e3fbcd6b-1766766162101.png"
          alt="Gold mining operation in Congo DRC showing workers processing raw gold ore in an open-pit mine"
          className="w-full h-full object-cover animate-cinematic"
          priority />
        
        {/* Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-accent via-accent/20 to-transparent opacity-80"></div>
        <div className="absolute inset-0 bg-accent/10 mix-blend-overlay"></div>
      </div>

      {/* Floating Live Gold Price Card (Top Right) with Enhanced Animation */}
      <div className="absolute top-32 right-6 md:right-12 z-20 animate-fade-in-up opacity-0 [animation-delay:2.5s]">
        <div className="glass-card-dark px-6 py-4 rounded-2xl border border-white/10 shadow-2xl min-w-[280px] hover-lift hover-glow transition-smooth">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-body font-medium uppercase tracking-widest text-white/60">
              Live Gold Price
            </span>
            <span className="w-2 h-2 rounded-full bg-success animate-pulse"></span>
          </div>
          <div className="flex items-baseline gap-2 mb-1">
            <span className="text-3xl font-display font-bold text-white animate-number-counter">
              ${goldPrice.price.toLocaleString()}
            </span>
            <span className="text-sm font-body text-white/80">/oz</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon
              name={goldPrice.change >= 0 ? 'ArrowTrendingUpIcon' : 'ArrowTrendingDownIcon'}
              size={16}
              className={goldPrice.change >= 0 ? 'text-success' : 'text-error'} />
            
            <span
              className={`text-sm font-body font-medium ${
              goldPrice.change >= 0 ? 'text-success' : 'text-error'}`
              }>
              
              {goldPrice.change >= 0 ? '+' : ''}
              {goldPrice.change.toFixed(2)} ({goldPrice.changePercent >= 0 ? '+' : ''}
              {goldPrice.changePercent.toFixed(2)}%)
            </span>
          </div>
          <div className="mt-3 pt-3 border-t border-white/10">
            <span className="text-xs font-body text-white/40">Last updated: Just now</span>
          </div>
        </div>
      </div>

      {/* Floating Location Badge (Top Left) with Enhanced Animation */}
      <div className="absolute top-32 left-6 md:left-12 z-20 animate-fade-in-up opacity-0 [animation-delay:2.2s]">
        <div className="glass-card-dark px-6 py-3 rounded-full border border-white/10 flex items-center gap-3 hover-lift transition-smooth">
          <Icon name="MapPinIcon" size={20} className="text-primary" />
          <span className="text-sm font-body font-medium text-white">
            North Kivu, Butembo • DRC
          </span>
        </div>
      </div>

      {/* Floating Certification Badges (Bottom) with Stagger Animation */}
      <div className="absolute bottom-40 left-6 md:left-12 z-20 flex gap-4 stagger-children">
        <div className="glass-card-dark px-4 py-2 rounded-lg border border-white/10 flex items-center gap-2 hover-lift transition-smooth">
          <Icon name="CheckBadgeIcon" size={20} className="text-primary" />
          <span className="text-xs font-body font-medium text-white/90">Kimberley Process</span>
        </div>
        <div className="glass-card-dark px-4 py-2 rounded-lg border border-white/10 flex items-center gap-2 hover-lift transition-smooth">
          <Icon name="ShieldCheckIcon" size={20} className="text-primary" />
          <span className="text-xs font-body font-medium text-white/90">LBMA Certified</span>
        </div>
        <div className="glass-card-dark px-4 py-2 rounded-lg border border-white/10 flex items-center gap-2 hover-lift transition-smooth">
          <Icon name="StarIcon" size={20} className="text-primary" />
          <span className="text-xs font-body font-medium text-white/90">ISO 9001</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto flex-grow flex flex-col justify-end pb-20">
        {/* Decorative Label */}
        <div className="flex items-center gap-3 mb-6 animate-slide-up opacity-0 [animation-delay:1.2s]">
          <span className="h-[1px] w-12 bg-white/60"></span>
          <span className="text-xs font-body font-medium uppercase tracking-widest text-white/80">
            Est. 2018
          </span>
        </div>

        {/* Hero Headline */}
        <h1 className="font-display font-bold text-white leading-[0.85] tracking-tight mb-8">
          <span className="block text-[12vw] md:text-7xl lg:text-8xl animate-slide-up opacity-0 [animation-delay:1.4s] text-shadow-strong">
            Premium Gold Export
          </span>
          <div className="flex items-baseline gap-4 md:gap-6 -mt-2 md:-mt-4 animate-slide-up opacity-0 [animation-delay:1.6s]">
            <span className="text-[12vw] md:text-7xl lg:text-8xl font-accent italic font-light text-white/60 text-shadow-strong">
              from the
            </span>
          </div>
          <span className="block text-[12vw] md:text-7xl lg:text-8xl animate-slide-up opacity-0 [animation-delay:1.8s] text-shadow-strong">
            Heart of Congo
          </span>
        </h1>

        {/* Subheadline */}
        <p className="text-lg md:text-xl text-white/90 font-body font-light leading-relaxed mb-12 max-w-2xl animate-slide-up opacity-0 [animation-delay:2s] text-shadow">
          Transparent pricing. International compliance. Direct from source.
        </p>

        {/* CTAs with Enhanced Hover Effects */}
        <div className="flex flex-col sm:flex-row gap-4 animate-slide-up opacity-0 [animation-delay:2.2s]">
          <button
            onClick={handleRequestQuote}
            className="group px-8 py-4 bg-primary text-primary-foreground rounded-full font-body font-semibold text-base shadow-gold-lg hover:shadow-gold transition-smooth hover:scale-105 hover-glow flex items-center justify-center gap-2">
            
            Request Quote
            <Icon
              name="ArrowRightIcon"
              size={20}
              className="group-hover:translate-x-1 transition-transform" />
            
          </button>
          <button
            onClick={handleViewPrices}
            className="group px-8 py-4 border-2 border-primary text-white rounded-full font-body font-semibold text-base hover:bg-primary/10 transition-smooth hover:scale-105 flex items-center justify-center gap-2">
            
            View Live Prices
            <Icon
              name="ChartBarIcon"
              size={20}
              className="group-hover:translate-x-1 transition-transform" />
            
          </button>
        </div>
      </div>

      {/* Scroll Indicator with Float Animation */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-slide-up opacity-0 [animation-delay:2.5s]">
        <span className="text-xs font-body uppercase tracking-widest text-white/40 animate-float">Scroll</span>
        <div className="w-[1px] h-12 bg-gradient-to-b from-white to-transparent animate-float"></div>
      </div>
    </section>);

};

export default HeroSection;